import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useI18n } from "@/contexts/I18nContext";

interface CommentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (comment: string) => void;
    isLoading: boolean;
    existingComment?: string | null;
}

export function CommentModal({
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    existingComment
}: CommentModalProps) {
    const [comment, setComment] = useState(existingComment || "");
    const { t } = useI18n();
    const { themeColor } = useColor();
    const APP_COLOR = appConfig.features.isDemoMode
        ? themeColor
        : appConfig.themeColor;

    // Sync internal state with existingComment prop
    useEffect(() => {
        setComment(existingComment || "");
    }, [existingComment]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden'; // Prevent background scroll
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleSubmit = () => {
        if (comment.trim()) {
            onSubmit(comment.trim());
        }
    };

    const handleClose = () => {
        setComment(existingComment || "");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={isLoading}
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">{t("commentModal.close")}</span>
                </button>

                {/* Header */}
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {existingComment ? t("commentModal.editTitle") : t("commentModal.addTitle")}
                    </h2>
                </div>

                {/* Content */}
                <div className="mb-6">
                    <Textarea
                        placeholder={t("commentModal.placeholder")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="min-h-[100px]"
                        maxLength={1000}
                        disabled={isLoading}
                    />
                    <div className="text-xs text-gray-500 mt-2">
                        {t("commentModal.characters", { count: comment.length })}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="text-black hover:text-black"
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleSubmit}
                        disabled={isLoading || !comment.trim()}
                        className="text-black hover:text-black"
                        style={{
                            backgroundColor: APP_COLOR,
                            borderColor: APP_COLOR,
                            color: 'white'
                        }}
                        onMouseEnter={(e) => {
                            if (!isLoading && comment.trim()) {
                                e.currentTarget.style.backgroundColor = APP_COLOR;
                                e.currentTarget.style.filter = 'brightness(0.8)';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = APP_COLOR;
                                e.currentTarget.style.borderWidth = '1px';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isLoading && comment.trim()) {
                                e.currentTarget.style.backgroundColor = APP_COLOR;
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = APP_COLOR;
                                e.currentTarget.style.borderWidth = '1px';
                                e.currentTarget.style.filter = 'none';
                            }
                        }}
                    >
                        {isLoading ? t("common.saving") : t("commentModal.save")}
                    </Button>
                </div>
            </div>
        </div>
    );
} 
